import {ThreeTsl as TSL} from "#material/three/exports"
import type {Three as THREE} from "#material/three/exports"
import {dFdxFloat, dFdyFloat} from "#material/three/gradient"
import type {TextureNodeLike} from "@cm/three-texture-streaming"

// Bump Mapping Unparametrized Surfaces on the GPU by Morten S. Mikkelsen
// https://mmikk.github.io/papers3d/mm_sfgrad_bump.pdf
const threeMikkelsen = TSL.Fn(([dHdxy]: [dHdxy: THREE.Node<"vec2">]) => {
    const vSigmaX = TSL.positionView.dFdx().normalize()
    const vSigmaY = TSL.positionView.dFdy().normalize()
    const vN = TSL.normalView

    const R1 = vSigmaY.cross(vN)
    const R2 = vN.cross(vSigmaX)

    // faceDirection is typed as Node without a type parameter — cast to float explicitly
    const fDet = vSigmaX.dot(R1).mul(TSL.faceDirection as THREE.Node<"float">)

    // Use vec3.mul(float) operand order; float.mul(vec3) lacks a TypeScript overload
    const vGrad = R1.mul(dHdxy.x).add(R2.mul(dHdxy.y)).mul(fDet.sign())

    return vN.mul(fDet.abs()).sub(vGrad).normalize()
})

// Procedural / virtual path: dFdx/dFdy in screen space feed directly into Mikkelsen
// (self-consistent — vSigmaX/Y are in the same screen-space coordinate system).
export const threeBumpMap = TSL.Fn(([h, scale]: [h: THREE.Node<"float">, scale: THREE.Node<"float">]) => {
    const dHdxy = TSL.vec2(dFdxFloat(h), dFdyFloat(h)).mul(scale)
    return threeMikkelsen(dHdxy)
})

// TextureNode path: screen-space forward differences feed directly into Mikkelsen.
// Samples at uv, uv + dFdx(uv), uv + dFdy(uv) — one screen pixel apart.
// Self-consistent with Mikkelsen (same screen-space coordinate system as vSigmaX/Y),
// moiré-free with nearest-neighbour filtering, and requires no image dimensions.
export const threeBumpMapFromTexture = TSL.Fn(([tex, uv, scale]: [tex: TextureNodeLike, uv: THREE.Node<"vec2">, scale: THREE.Node<"float">]) => {
    const h0 = tex.sample(uv).r
    const dHdxy = TSL.vec2(tex.sample(uv.add(TSL.dFdx(uv))).r.sub(h0), tex.sample(uv.add(TSL.dFdy(uv))).r.sub(h0)).mul(scale)
    return threeMikkelsen(dHdxy)
})
